import * as z from "zod";

// *** NOTE: importing from @shared/utils is not working ***
// *** so we are importing with relative path the functions here ***
// It create a circular dependency
import { addTenYears, currentDate, isValidYear } from "../../utils/dates";
import { getAsNumber } from "../../utils/get-as-number";
import { isEmpty } from "../../utils/is-empty";
import {
  FundPurposeEnum,
  HeatingTypeEnum,
  MAX_LENDING_AMOUNT_RATIO,
  SewageTypeEnum,
  TargetPropertyStyleEnum,
  TargetPropertyTenureEnum,
  TargetPropertyTypeEnum,
  WaterTypeEnum,
  addressSchema,
  rentalIncomeAmountFrequency,
  rentalIncomeAmountFrequencyOptional,
} from "../application";
import { amountSchema, LenderEnum } from "../generic";
import {
  amountUnitSchema,
  coOwnersheepFeesOptional,
  garageSchema,
} from "../target-property";

const DATE_VALIDATION_REGEX =
  /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/;

export const purchaseDateSchema = z
  .string({
    error: () => "form:error.required",
  })
  .trim()
  .regex(DATE_VALIDATION_REGEX, {
    message: "form:error.invalidDate",
  })
  .refine(
    (value) => {
      // Get the current date in YYYY-MM-DD format
      return value <= currentDate;
    },
    {
      message: "form:error.invalidDate",
    }
  );

export const maturityDateSchema = z
  .string({
    error: () => "form:error.required",
  })
  .trim()
  .regex(DATE_VALIDATION_REGEX, {
    message: "form:error.invalidDate",
  })
  .refine((value) => value >= currentDate, {
    message: "form:error.dateShouldBeFuture",
  })
  .refine((value) => value <= addTenYears(), {
    message: "form:error.invalidDate",
  });

export const additionalFundAmountSchema = z.preprocess(
  (v) => (isEmpty(v) ? null : getAsNumber(v)),
  z
    .number({
      error: () => "form:error.required",
      coerce: false,
    })
    .min(1, { message: "form:error.required" })
);

export const baseSchema = (tenant?: string) =>
  z
    .object({
      setRegisteredAddress: z.any().optional(),
      estimatedPropertyValue: amountSchema,
      purchasePrice: amountSchema,
      rentalIncome: rentalIncomeAmountFrequencyOptional.nullable().optional(),
      purchaseDate: purchaseDateSchema,
      purpose: z.enum(
        [
          "OWNER_OCCUPIED",
          "OWNER_OCCUPIED_AND_RENTAL",
          "RENTAL",
          "SECONDARY_RESIDENCE",
        ],
        {
          error: () => "form:error.required",
        }
      ),
      address: addressSchema,
      selectedAddress: z.any(),
      yearBuilt: z.preprocess(
        (v) => (isEmpty(v) ? null : getAsNumber(v)),
        z.number({ error: () => "form:error.required" }).refine(
          (data) => {
            return isValidYear(data);
          },
          {
            message: "form:error.invalidYear",
          }
        )
      ),
      propertyStyle: z.enum(TargetPropertyStyleEnum, {
        error: () => "form:error.required",
      }),
      propertyType: z.enum(TargetPropertyTypeEnum, {
        error: () => "form:error.required",
      }),
      tenure: z.enum(TargetPropertyTenureEnum, {
        error: () => "form:error.required",
      }),
      schoolAndMunicipalTaxes: z.object({
        amount: amountSchema,
        year: z.preprocess(
          (v) => (isEmpty(v) ? null : getAsNumber(v)),
          z.number({
            error: () => "form:error.required",
          })
        ),
      }),
      livingSpace: amountUnitSchema,
      lotSize: amountUnitSchema,
      heatingType: z.enum(HeatingTypeEnum, {
        error: () => "form:error.required",
      }),
      heatingCostIncluded: z.boolean().nullable(),
      heatingCost: z
        .object({
          amount: z
            .preprocess(
              (v) => (isEmpty(v) ? null : getAsNumber(v)),
          z
            .number({
              error: (issue) =>
                issue.input === undefined ? "required" : "type",
              coerce: false,
            })
                .max(9_999_999, {
                  message: "form:error.maxDollarAmount",
                })
                .nullable()
            )
            .optional(),
          frequency: z.preprocess(
            (v) => (isEmpty(v) ? "" : v),
          z
            .enum(
              [
                "WEEKLY",
                "BIWEEKLY",
                "MONTHLY",
                "SEMIMONTHLY",
                "SEMIANNUALLY",
                "ANNUALLY",
                "",
              ],
              {
                error: () => "form:error.required",
              }
            )
            .optional()
        ),
        })
        .nullable(),
      coOwnershipFees: coOwnersheepFeesOptional,
      waterType: z.enum(WaterTypeEnum, {
        error: () => "form:error.required",
      }),
      sewageType: z.enum(SewageTypeEnum, {
        error: () => "form:error.required",
      }),
      garage: garageSchema,
      mortgages: z
        .object({
          balance: amountSchema,
          interestRate: z
            .number({
              error: () => "form:error.required",
              coerce: false,
            })
            .max(99.99, { message: "form:error.maxInterest" })
            .refine(
              (value) => {
                return value !== null && value > 0;
              },
              {
                message: "form:error.required",
              }
            ),
          interestRateType: z.enum(
            ["FIXED", "VARIABLE", "ADJUSTABLE", "CAPPED_VARIABLE"],
            {
              error: () => "form:error.required",
            }
          ),
          lender: z.enum(LenderEnum, {
            error: () => "form:error.required",
          }),
          maturityDate: maturityDateSchema,
          mortgageType: z.enum(["STANDARD", "HELOC"], {
            error: () => "form:error.required",
          }),
          payment: z.object({
            amount: z.preprocess(
              (v) => (isEmpty(v) ? null : getAsNumber(v)),
              z
                .number({
                  error: () => "form:error.missingAmount",
                  coerce: false,
                })
                .max(9_999_999, { message: "form:error.maxDollarAmount" })
                .min(1, { message: "form:error.missingAmount" })
            ),
            frequency: z.enum(
              [
                "WEEKLY",
                "ACCELERATED_WEEKLY",
                "BIWEEKLY",
                "ACCELERATED_BIWEEKLY",
                "SEMIMONTHLY",
                "MONTHLY",
              ],
              {
                error: () => "form:error.missingFrequency",
              }
            ),
          }),
          termType: z.enum(["OPEN", "CLOSED", "CONVERTABLE"], {
            error: () => "form:error.required",
          }),
        })
        .array()
        .optional()
        .nullable(),
      additionalFundAmount: z.any(),
      fundPurpose: z.enum(FundPurposeEnum, {
        error: () => "form:error.required",
      }),
    })
    .superRefine((values, ctx) => {
      const propertyValue = values.estimatedPropertyValue;
      const mortgageBalance = values.mortgages?.[0].balance || 0;

      const percentage = MAX_LENDING_AMOUNT_RATIO;

      const maxAdditionalFund = propertyValue * percentage - mortgageBalance;

      const isOk = z
        .number()
        .max(maxAdditionalFund > 0 ? Math.round(maxAdditionalFund) : 0)
        .safeParse(values.additionalFundAmount);

      if (!isOk.success) {
        isOk.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `form:error.customMaxAmount`,
            path: ["additionalFundAmount", ...issue.path],
          });
        });
      }
    })
    .and(purposeUnion);

export const purposeUnion = z.discriminatedUnion("purpose", [
  z.object({
    purpose: z.enum(["OWNER_OCCUPIED", "SECONDARY_RESIDENCE"], {
      error: () => "form:error.required",
    }),
    rentalIncome: rentalIncomeAmountFrequencyOptional.nullable().optional(),
  }),
  z.object({
    purpose: z.enum(["OWNER_OCCUPIED_AND_RENTAL", "RENTAL"], {
      error: () => "form:error.required",
    }),
    rentalIncome: rentalIncomeAmountFrequency,
  }),
]);

// create has additional field setRegisteredAddress
export const createPropertySchema = (tenant?: string) =>
  z
    .discriminatedUnion("setRegisteredAddress", [
      z.object({
        setRegisteredAddress: z.literal(true),
      }),
      z.object({
        setRegisteredAddress: z.literal(false),
        address: addressSchema,
      }),
    ])
    .and(baseSchema(tenant));

export const createRefinancePropertySchema = (tenant?: string) =>
  z.union([baseSchema(tenant), createPropertySchema(tenant)]);

export const refinancePropertySchema = createRefinancePropertySchema();

export type RefinancePropertyForm = z.infer<typeof refinancePropertySchema>;
